/**
 * NMS Store Launch · Store map
 *
 * Vanilla port of the design's canvas map without d3: state outlines are
 * pre-projected (Web Mercator) in assets/nms-sl-india-geo.json, so here we
 * only scale them into the box and project store lat/lng with the same
 * constants.
 *
 * Performance notes:
 * - Geometry is fetched only when the map is within ~900px of the viewport.
 * - The draw loop runs at ~30fps and only while the canvas is visible and the
 *   tab is active; once the intro wave settles the static base layer is
 *   cached in an offscreen canvas and only arcs/pins are redrawn.
 * - Reduced motion draws a single static frame.
 */
(() => {
  const C = { ink: '#1C1717', red: '#B4231F', green: '#1F5F42' };
  const clamp01 = x => (x < 0 ? 0 : x > 1 ? 1 : x);
  const ease = t => (t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const lerp = (a, b, t) => a + (b - a) * t;

  class NMSSlMap extends window.NMSBaseClass {
    constructor(element, service) {
      super();
      this.element = element;
      this.sl = service;
      this.labels = element.dataset;
      this.controller = new AbortController();
      this.zoom = null;
      this.pop = null;
      this.picked = null;
      this.hover = null;
      this.hoverState = null;
      this.hits = [];
      this.pillHits = [];
    }

    init() {
      super.init();
      const { signal } = this.controller;
      this.sl.stateIcons = null; // re-read the State icon blocks (the editor re-inits on section reload)
      this.stores = this.sl.stores;
      this.last = this.stores.length - 1;
      this.canvas = this.refs.canvas;
      this.ctx = this.canvas.getContext('2d');
      this.reduced = this.sl.reducedMotion;
      this.counts = {};
      this.stores.forEach(s => {
        if (!s.mock && s.state) this.counts[s.state] = (this.counts[s.state] || 0) + 1;
      });

      this.#renderChips();
      this.#renderLegend();
      this.#renderOverlay();
      this.sl.on('tick', () => this.#renderLegend(), signal);
      this.sl.on('select', ({ index, source }) => {
        // A store tapped on the map (mobile opens the sheet, not the popup) keeps the only label.
        if (source === 'map' || source === 'state') this.picked = index;
        this.#draw();
      }, signal);
      this.sl.on('map:zoom', ({ state }) => this.zoomTo(state), signal);
      this.sl.on('map:show', ({ index }) => this.#goTo(index), signal);

      this.canvas.addEventListener('click', event => this.#onClick(event), { signal });
      this.canvas.addEventListener('mousemove', event => this.#onMove(event), { signal });
      this.canvas.addEventListener('mouseleave', () => {
        this.hover = null;
        this.hoverState = null;
        this.#draw();
      }, { signal });
      this.refs.zoomOut.addEventListener('click', () => {
        // Back to All India also closes the state's sheet (and a store opened from it) on mobile.
        const kind = this.sl.sheet?.kind;
        if (kind === 'state' || kind === 'store') this.sl.closeSheet(true);
        this.zoomTo(null);
      }, { signal });
      // Prestige's hide-on-scroll header slides back in whenever the pointer is in the top 100px of
      // the window (theme.js, Header detectMousePosition_fn on window pointermove) and covers whatever
      // sits there. Keep it away while the pointer is over the map and state list, so the zoom pills
      // and chips near the top of the screen stay clickable and scrollable.
      this.element.querySelector('.nms-sl-map__grid')?.addEventListener('pointermove', event => event.stopPropagation(), { signal });
      this.refs.pop.addEventListener('click', event => this.#onPopClick(event), { signal });
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || this.sl.sheet?.kind) return;
        if (this.pop !== null) this.#closePop();
        else if (this.zoom) this.zoomTo(null);
      }, { signal });

      // Load geometry shortly before the map scrolls into view.
      const start = () => {
        if (this.geoRequested) return;
        this.geoRequested = true;
        this.near?.disconnect();
        this.#loadGeo();
      };
      this.near = new IntersectionObserver(entries => entries.some(e => e.isIntersecting) && start(), { rootMargin: '900px 0px' });
      this.near.observe(this.refs.wrap);
      this.idleTimer = setTimeout(start, 6000);

      this.seen = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        // Mobile: the state sheet belongs to the map, so it closes once ~70% of the map is scrolled out
        // of view. Only on the way out: opening a state scrolls the map in and must not close it.
        const was = this.mapRatio ?? 0;
        this.mapRatio = entry.intersectionRatio;
        if (was >= .3 && entry.intersectionRatio < .3 && this.sl.sheet?.kind === 'state' && this.sl.isMobile()) this.sl.closeSheet(true);
        if (entry.intersectionRatio >= .3 && !this.playStart) this.playStart = performance.now();
        if (!entry.isIntersecting) this.playStart = this.reduced ? 1 : 0;
        if (this.visible) this.#kick();
      }, { threshold: [0, .3] });
      this.seen.observe(this.refs.wrap);

      this.resize = new ResizeObserver(() => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          this.#layout();
          this.#draw();
        }, 120);
      });
      this.resize.observe(this.refs.wrap);
    }

    /* ---------------- Data / layout ---------------- */

    async #loadGeo() {
      try {
        const res = await fetch(this.sl.config.assets.geo);
        this.geo = await res.json();
      } catch (error) {
        console.warn('[nms-sl] map geometry failed to load', error);
        return;
      }
      this.india = new Path2D(this.geo.india);
      this.statesBase = this.geo.states.map(st => {
        const nums = st.d.match(/-?\d+(\.\d+)?/g).map(Number);
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
        for (let k = 0; k < nums.length; k += 2) {
          x0 = Math.min(x0, nums[k]); x1 = Math.max(x1, nums[k]);
          y0 = Math.min(y0, nums[k + 1]); y1 = Math.max(y1, nums[k + 1]);
        }
        return { name: st.name, path: new Path2D(st.d), box: [x0, y0, x1, y1] };
      });
      this.#layout();
      this.#kick();
    }

    /** Geo units → CSS pixels for the current box. */
    #fit(x, y) {
      const F = this.F;
      return [F.x0 + x * F.k, F.y0 + y * F.k];
    }

    #project(lng, lat) {
      const g = this.geo;
      const x = g.k * (lng * Math.PI / 180) + g.tx;
      const y = g.ty - g.k * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 180) / 2));
      return this.#fit(x, y);
    }

    #layout() {
      const rect = this.refs.wrap.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      if (!w || !h) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      const mobile = w < 600;
      this.L = { w, h, dpr, mobile };
      this.#closePop();
      this.#renderOverlay();
      if (!this.geo) return;

      const pad = mobile ? 16 : 40;
      const top = mobile ? 64 : 72;
      const bottom = mobile ? 24 : 32;
      const k = Math.min((w - 2 * pad) / this.geo.w, (h - top - bottom) / this.geo.h);
      this.F = { k, x0: pad + (w - 2 * pad - this.geo.w * k) / 2, y0: top + (h - top - bottom - this.geo.h * k) / 2 };
      const m = new DOMMatrix([k, 0, 0, k, this.F.x0, this.F.y0]);
      const scaled = base => {
        const p = new Path2D();
        p.addPath(base, m);
        return p;
      };
      this.indiaPath = scaled(this.india);

      const origin = this.stores[0]?.hasGeo ? this.stores[0] : this.stores.find(s => s.hasGeo);
      this.points = this.stores.map(s => (s.hasGeo ? this.#project(s.lng, s.lat) : null));
      this.origin = origin ? this.#project(origin.lng, origin.lat) : [w / 2, h / 2];

      // Halftone dot field inside India; dots glow near stores.
      const ctx = this.ctx;
      const step = mobile ? 6 : 8;
      const dots = [];
      let maxD = 1;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      for (let y = step / 2; y < h; y += step) {
        for (let x = step / 2; x < w; x += step) {
          if (!ctx.isPointInPath(this.indiaPath, x, y)) continue;
          let dm = 1e9;
          for (const p of this.points) {
            if (!p) continue;
            const dx = p[0] - x;
            if (dx > 40 || dx < -40) continue;
            const dy = p[1] - y;
            if (dy > 40 || dy < -40) continue;
            dm = Math.min(dm, dx * dx + dy * dy);
          }
          const d = Math.hypot(x - this.origin[0], y - this.origin[1]);
          if (d > maxD) maxD = d;
          dots.push({ x, y, d, g: clamp01(1 - Math.sqrt(dm) / (mobile ? 26 : 38)) });
        }
      }
      dots.forEach(o => (o.d /= maxD));
      this.dots = dots;

      this.states = this.statesBase.map(st => {
        const [a, b] = [this.#fit(st.box[0], st.box[1]), this.#fit(st.box[2], st.box[3])];
        const cx = (a[0] + b[0]) / 2;
        const cy = (a[1] + b[1]) / 2;
        return { name: st.name, path: scaled(st.path), box: [a, b], d: clamp01(Math.hypot(cx - this.origin[0], cy - this.origin[1]) / maxD), n: this.counts[st.name] || 0 };
      });
      this.zTo = this.zoom ? this.#fitZoom(this.zoom) : null;
      this.zStart = 0;
      this.ready = true;
      this.baseKey = '';
      this.homeKey = '';
    }

    /* ---------------- Zoom ---------------- */

    #fitZoom(name) {
      const L = this.L;
      const st = this.states?.find(s => s.name === name);
      if (!L || !st) return { k: 1, x: 0, y: 0 };
      const [[x0, y0], [x1, y1]] = st.box;
      const top = L.mobile ? 70 : 80;
      const bottom = L.mobile ? (this.sl.sheet?.kind === 'state' ? 120 : 30) : 40;
      const pad = L.mobile ? 24 : 60;
      const k = Math.max(1.2, Math.min(L.mobile ? 7 : 9, Math.min((L.w - 2 * pad) / Math.max(1, x1 - x0), (L.h - top - bottom) / Math.max(1, y1 - y0))));
      return { k, x: L.w / 2 - ((x0 + x1) / 2) * k, y: top + (L.h - top - bottom) / 2 - ((y0 + y1) / 2) * k };
    }

    #currentZoom(now) {
      const to = this.zTo || { k: 1, x: 0, y: 0 };
      if (!this.zStart || !this.zFrom || !this.L) return to;
      const t = this.reduced ? 1 : ease(clamp01((now - this.zStart) / 950));
      if (t >= 1) {
        this.zStart = 0;
        return to;
      }
      const f = this.zFrom;
      const cx = this.L.w / 2;
      const cy = this.L.h / 2;
      const c0 = [(cx - f.x) / f.k, (cy - f.y) / f.k];
      const c1 = [(cx - to.x) / to.k, (cy - to.y) / to.k];
      const k = Math.exp(lerp(Math.log(f.k), Math.log(to.k), t));
      return { k, x: cx - lerp(c0[0], c1[0], t) * k, y: cy - lerp(c0[1], c1[1], t) * k };
    }

    zoomTo(name) {
      const st = name && this.states?.find(s => s.name === name && s.n > 0);
      const next = st ? st.name : null;
      if (this.states) {
        this.zFrom = this.#currentZoom(performance.now());
        this.zTo = st ? this.#fitZoom(st.name) : null;
        this.zStart = performance.now();
      }
      this.#closePop();
      if (next !== this.zoom) {
        this.zoom = next;
        this.#renderChips();
        this.#renderOverlay();
      }
      this.#kick();
    }

    /* ---------------- Drawing ---------------- */

    #kick() {
      if (this.reduced || !this.visible) {
        this.zStart = 0;
        this.#draw();
        return;
      }
      if (this.loop) return;
      const frame = now => {
        this.loop = 0;
        if (!this.visible || document.hidden) return;
        const fast = this.zStart || this.hover !== null;
        if (fast || !this.lastDraw || now - this.lastDraw > 31) {
          this.lastDraw = now;
          this.#draw(now);
        }
        this.loop = requestAnimationFrame(frame);
      };
      this.loop = requestAnimationFrame(frame);
    }

    #drawBase(ctx, Z, wave, hq) {
      const zk = Z.k;
      const zf = clamp01((zk - 1) / 1.4);
      ctx.save();
      if (hq) {
        ctx.shadowColor = 'rgba(60,40,20,.10)';
        ctx.shadowBlur = 40 * zk;
        ctx.shadowOffsetY = 14 / zk;
      }
      ctx.globalAlpha = .4 + .6 * wave;
      ctx.fillStyle = '#EFE8DC';
      ctx.fill(this.indiaPath);
      ctx.restore();

      this.states.forEach(st => {
        const a = this.reduced ? 1 : clamp01((wave * 1.35 - st.d) / .35);
        if (a <= 0) return;
        const on = this.zoom === st.name;
        const hv = this.hoverState === st.name && st.n > 0;
        ctx.globalAlpha = a * (this.zoom && !on ? .55 : 1);
        ctx.fillStyle = on ? '#FFFFFF' : hv ? '#F9D9C6' : st.n ? '#F5E3D7' : '#ECE5D9';
        ctx.fill(st.path);
      });
      ctx.globalAlpha = wave;
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 1.6 / zk;
      ctx.lineJoin = 'round';
      this.states.forEach(st => ctx.stroke(st.path));

      const dotA = 1 - zf;
      if (dotA > .02) {
        const r = this.L.mobile ? 1.05 : 1.3;
        const buckets = [[], [], [], []];
        for (const o of this.dots) {
          const a = clamp01((wave * 1.25 - o.d) / .25);
          if (a <= 0) continue;
          buckets[o.g > .66 ? 3 : o.g > .33 ? 2 : o.g > .05 ? 1 : 0].push(o, a);
        }
        const colours = ['rgba(120,110,95,.16)', 'rgba(180,35,31,.30)', 'rgba(180,35,31,.55)', 'rgba(180,35,31,.85)'];
        ctx.globalAlpha = dotA;
        buckets.forEach((bucket, bi) => {
          ctx.fillStyle = colours[bi];
          ctx.beginPath();
          for (let i = 0; i < bucket.length; i += 2) {
            const o = bucket[i];
            const rr = (r + (bi ? bi * .3 : 0)) * (.4 + .6 * bucket[i + 1]);
            ctx.moveTo(o.x + rr, o.y);
            ctx.arc(o.x, o.y, rr, 0, 7);
          }
          ctx.fill();
        });
      }
      ctx.globalAlpha = wave;
      ctx.strokeStyle = 'rgba(28,23,23,.22)';
      ctx.lineWidth = 1 / zk;
      ctx.stroke(this.indiaPath);
      if (this.zoom) {
        const st = this.states.find(s => s.name === this.zoom);
        if (st) {
          ctx.globalAlpha = zf;
          ctx.strokeStyle = C.ink;
          ctx.lineWidth = 1.6 / zk;
          ctx.stroke(st.path);
        }
      }
      ctx.globalAlpha = 1;
    }

    /** Map label. `chevron` draws a caret after the text (any typed "›" in the label is dropped). */
    #pill(ctx, x, y, text, dot, side, dy = 0, chevron = false) {
      ctx.font = '500 13px Roboto, system-ui, sans-serif';
      if (chevron) text = text.replace(/s*[›>»]s*$/, '');
      const tw = ctx.measureText(text).width;
      const pw = tw + (dot ? 32 : 22) + (chevron ? 14 : 0);
      const ph = 30;
      const yy = y + dy;
      let px = side === 'l' ? x - 16 - pw : x + 16;
      if (px + pw > this.L.w - 8) px = x - 16 - pw;
      if (px < 8) px = x + 16;
      // Narrow maps: keep the label inside the box even if it has to overlap its pin side.
      px = Math.max(8, Math.min(px, this.L.w - 8 - pw));
      ctx.save();
      ctx.shadowColor = 'rgba(28,23,23,.16)';
      ctx.shadowBlur = 16;
      ctx.shadowOffsetY = 4;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(px, yy - ph / 2, pw, ph, 15);
      else ctx.rect(px, yy - ph / 2, pw, ph);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
      ctx.restore();
      if (dot) {
        ctx.beginPath();
        ctx.arc(px + 14, yy, 4, 0, 7);
        ctx.fillStyle = dot;
        ctx.fill();
      }
      ctx.fillStyle = C.ink;
      ctx.textBaseline = 'middle';
      const tx = px + (dot ? 24 : 11);
      ctx.fillText(text, tx, yy + .5);
      if (chevron) {
        const cx = tx + tw + 8;
        ctx.beginPath();
        ctx.moveTo(cx, yy - 4);
        ctx.lineTo(cx + 4, yy);
        ctx.lineTo(cx, yy + 4);
        ctx.strokeStyle = C.ink;
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
      }
      return { x: px, y: yy - ph / 2, w: pw, h: ph };
    }

    #draw(now = performance.now()) {
      const L = this.L;
      const ctx = this.ctx;
      if (!L || !ctx) return;
      const { w, h, dpr } = L;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      if (!this.ready) return;

      const S = this.stores;
      const P = this.points;
      const P0 = this.origin;
      const LAST = this.last;
      const progress = this.reduced ? 1 : this.playStart ? clamp01((now - this.playStart) / 3400) : 0;
      const wave = clamp01(progress / .45);
      const k = clamp01((progress - .3) / .7) * (LAST + 7);
      const Z = this.#currentZoom(now);
      const zk = Z.k;
      const zf = clamp01((zk - 1) / 1.4);

      // Base layer: cached once settled, scaled home-cache while zooming, live during the intro wave.
      if (wave >= 1 && !this.zStart) {
        const key = [this.canvas.width, this.canvas.height, this.zoom, this.hoverState, zk.toFixed(3), Z.x.toFixed(1), Z.y.toFixed(1)].join('|');
        if (this.baseKey !== key) {
          const bc = this.baseCanvas || (this.baseCanvas = document.createElement('canvas'));
          bc.width = this.canvas.width;
          bc.height = this.canvas.height;
          const b = bc.getContext('2d');
          b.setTransform(dpr * zk, 0, 0, dpr * zk, dpr * Z.x, dpr * Z.y);
          this.#drawBase(b, Z, 1, true);
          this.baseKey = key;
        }
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(this.baseCanvas, 0, 0);
      } else if (wave >= 1) {
        const hk = `${this.canvas.width}x${this.canvas.height}`;
        if (!this.homeCanvas || this.homeKey !== hk) {
          const hc = this.homeCanvas || (this.homeCanvas = document.createElement('canvas'));
          hc.width = this.canvas.width;
          hc.height = this.canvas.height;
          const b = hc.getContext('2d');
          b.setTransform(dpr, 0, 0, dpr, 0, 0);
          const saved = this.zoom;
          this.zoom = null;
          this.#drawBase(b, { k: 1, x: 0, y: 0 }, 1, false);
          this.zoom = saved;
          this.homeKey = hk;
        }
        ctx.setTransform(zk, 0, 0, zk, dpr * Z.x, dpr * Z.y);
        ctx.imageSmoothingQuality = 'low';
        ctx.drawImage(this.homeCanvas, 0, 0);
      } else {
        ctx.setTransform(dpr * zk, 0, 0, dpr * zk, dpr * Z.x, dpr * Z.y);
        this.#drawBase(ctx, Z, wave, false);
      }

      // Arcs from store 01 with a travelling head.
      ctx.setTransform(dpr * zk, 0, 0, dpr * zk, dpr * Z.x, dpr * Z.y);
      ctx.globalAlpha = 1;
      ctx.lineCap = 'round';
      S.forEach((s, i) => {
        const P1 = P[i];
        if (!P1 || i === 0 || i === LAST) return;
        const dist = Math.hypot(P1[0] - P0[0], P1[1] - P0[1]);
        if (dist < 4) return;
        const t = clamp01((k - i) / 8);
        if (t <= 0) return;
        const ctrl = [(P0[0] + P1[0]) / 2, (P0[1] + P1[1]) / 2 - dist * .32];
        const bz = u => {
          const m = 1 - u;
          return [m * m * P0[0] + 2 * m * u * ctrl[0] + u * u * P1[0], m * m * P0[1] + 2 * m * u * ctrl[1] + u * u * P1[1]];
        };
        const seg = (a, b, n) => {
          ctx.beginPath();
          for (let j = 0; j <= n; j++) {
            const p = bz(a + ((b - a) * j) / n);
            if (j) ctx.lineTo(p[0], p[1]);
            else ctx.moveTo(p[0], p[1]);
          }
        };
        const tt = ease(t);
        ctx.globalAlpha = this.zoom && s.state !== this.zoom ? .35 : 1;
        seg(0, tt, this.zStart ? 14 : 40);
        ctx.strokeStyle = 'rgba(180,35,31,.24)';
        ctx.lineWidth = 1.1 / zk;
        ctx.setLineDash([3 / zk, 4 / zk]);
        ctx.stroke();
        ctx.setLineDash([]);
        let head = null;
        if (t < 1) head = tt;
        else if (!this.reduced) {
          const ph = (now / 2800 + i * .137) % 1;
          const a0 = Math.max(0, ph - .28);
          const A = bz(a0);
          const B = bz(ph);
          seg(a0, ph, 18);
          const gr = ctx.createLinearGradient(A[0], A[1], B[0], B[1]);
          gr.addColorStop(0, 'rgba(180,35,31,0)');
          gr.addColorStop(1, 'rgba(180,35,31,1)');
          ctx.strokeStyle = gr;
          ctx.lineWidth = 2.4 / zk;
          ctx.stroke();
          head = ph;
          if (ph > .82) {
            const u = (ph - .82) / .18;
            ctx.beginPath();
            ctx.arc(P1[0], P1[1], (5 + 14 * u) / zk, 0, 7);
            ctx.strokeStyle = `rgba(180,35,31,${(.7 * (1 - u)).toFixed(3)})`;
            ctx.lineWidth = 1.5 / zk;
            ctx.stroke();
          }
        }
        if (head !== null) {
          const p = bz(head);
          ctx.beginPath();
          ctx.arc(p[0], p[1], 6 / zk, 0, 7);
          ctx.fillStyle = 'rgba(216,72,58,.22)';
          ctx.fill();
          ctx.beginPath();
          ctx.arc(p[0], p[1], 2.6 / zk, 0, 7);
          ctx.fillStyle = '#D8483A';
          ctx.fill();
        }
      });

      // Store pins (screen space so they keep their size while zoomed).
      ctx.globalAlpha = 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const toScreen = p => [p[0] * zk + Z.x, p[1] * zk + Z.y];
      const inView = q => q[0] > -20 && q[0] < w + 20 && q[1] > -20 && q[1] < h + 20;
      this.hits = [];
      this.pillHits = [];
      const big = 1 + .25 * zf;
      S.forEach((s, i) => {
        if (!P[i] || i === LAST) return;
        const p = toScreen(P[i]);
        if (!inView(p)) return;
        const t = i === 0 ? 1 : clamp01((k - i) / 8);
        if (t <= 0) return;
        const dim = this.zoom && s.state !== this.zoom;
        if (t > .7 && t < 1) {
          const u = (t - .7) / .3;
          ctx.beginPath();
          ctx.arc(p[0], p[1], 6 + 18 * u, 0, 7);
          ctx.strokeStyle = `rgba(180,35,31,${(1 - u).toFixed(3)})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
        const hv = this.hover === i;
        const rr = (hv ? 7.5 : 5) * big * ease(clamp01(t / .7));
        ctx.globalAlpha = dim ? .45 : 1;
        if (rr > 1) {
          ctx.beginPath();
          ctx.arc(p[0], p[1], rr + (hv ? 7 : 4), 0, 7);
          ctx.fillStyle = i === 0 ? 'rgba(31,107,69,.16)' : 'rgba(180,35,31,.16)';
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(p[0], p[1], rr, 0, 7);
        ctx.fillStyle = i === 0 ? C.green : C.red;
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#FFFFFF';
        ctx.stroke();
        ctx.globalAlpha = 1;
        if (!dim) this.hits.push({ i, x: p[0], y: p[1] });
      });

      // Once a store is tapped/clicked only that store keeps its label (also after its card or the
      // state list is closed, and when zooming in or out); the launch pin keeps just its beacon. While
      // the card is open the label moves to the side away from it.
      const open = this.pop !== null;
      const focus = open ? this.pop : this.picked ?? null;
      const focusSide = this.popSide === 'l' ? 'r' : 'l';
      const hovering = i => !open && this.hover === i;
      const taken = [];
      const free = r => !taken.some(o => r.x < o.x + o.w + 4 && r.x + r.w + 4 > o.x && r.y < o.y + o.h + 3 && r.y + r.h + 3 > o.y);
      const { fill } = this.sl.helpers;
      const launch = this.sl.launch;
      const fp = toScreen(P[LAST] || P0);
      if (inView(fp)) {
        const ph = this.reduced ? .3 : (now / 1700) % 1;
        for (const off of [0, .33, .66]) {
          const u = (ph + off) % 1;
          ctx.beginPath();
          ctx.arc(fp[0], fp[1], 10 + 36 * u, 0, 7);
          ctx.fillStyle = `rgba(180,35,31,${(.22 * (1 - u)).toFixed(3)})`;
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(fp[0], fp[1], 10, 0, 7);
        ctx.fillStyle = C.red;
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#FFFFFF';
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(fp[0], fp[1], 3.2, 0, 7);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
        this.hits.push({ i: LAST, x: fp[0], y: fp[1] });
        if (focus === null || focus === LAST || hovering(LAST)) {
          const r1 = this.#pill(ctx, fp[0], fp[1], fill(this.labels.pillLaunch, { number: launch.number, area: launch.area, date: launch.dateShort }), C.red, open ? focusSide : 'r', open ? 0 : -24, true);
          this.pillHits.push({ ...r1, i: LAST });
          taken.push(r1);
        }
      }
      const p01 = P[0] && toScreen(P[0]);
      if (p01 && inView(p01) && (focus === null || focus === 0 || hovering(0))) {
        const r0 = this.#pill(ctx, p01[0], p01[1], fill(this.labels.pillFirst, { label: this.sl.config.firstLabel || S[0].title }), C.green, open ? focusSide : 'l', open ? 0 : 24, true);
        this.pillHits.push({ ...r0, i: 0 });
        taken.push(r0);
      }

      const active = this.sl.activeIndex;
      const plain = i => i !== null && i !== 0 && i !== LAST;
      const tip = open ? (plain(focus) ? focus : null) : plain(this.hover) ? this.hover : focus !== null ? (plain(focus) ? focus : null) : plain(active) ? active : null;
      if (tip !== null && P[tip]) {
        const p = toScreen(P[tip]);
        if (inView(p)) {
          if (tip === active) {
            ctx.beginPath();
            ctx.arc(p[0], p[1], 13, 0, 7);
            ctx.strokeStyle = C.ink;
            ctx.lineWidth = 2;
            ctx.stroke();
          }
          taken.push(this.#pill(ctx, p[0], p[1], `${S[tip].num} · ${S[tip].label}`, C.ink, open ? focusSide : undefined));
        }
      }

      if (this.zoom && zf > .6 && !open) {
        ctx.globalAlpha = (zf - .6) / .4;
        ctx.font = '500 12px Roboto, system-ui, sans-serif';
        ctx.textBaseline = 'middle';
        S.forEach((s, i) => {
          if (s.state !== this.zoom || !P[i] || i === LAST || i === 0 || i === tip) return;
          const p = toScreen(P[i]);
          if (!inView(p)) return;
          const r = { x: p[0] + 12, y: p[1] - 12, w: ctx.measureText(s.title).width + 16, h: 24 };
          if (r.x + r.w > w - 6) r.x = p[0] - 12 - r.w;
          if (!free(r)) return;
          taken.push(r);
          ctx.fillStyle = 'rgba(255,255,255,.92)';
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(r.x, r.y, r.w, r.h, 12);
          else ctx.rect(r.x, r.y, r.w, r.h);
          ctx.fill();
          ctx.fillStyle = C.ink;
          ctx.fillText(s.title, r.x + 8, r.y + 12.5);
          this.pillHits.push({ ...r, i });
        });
        ctx.globalAlpha = 1;
      }
    }

    /* ---------------- Hit testing ---------------- */

    #point(event) {
      const b = this.canvas.getBoundingClientRect();
      return [event.clientX - b.left, event.clientY - b.top];
    }

    #hit(event) {
      const [x, y] = this.#point(event);
      const pill = this.pillHits.find(r => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);
      if (pill) return pill.i;
      let best = null;
      let bestD = this.sl.isMobile() ? 26 : 18;
      this.hits.forEach(t => {
        const d = Math.hypot(t.x - x, t.y - y) - (t.i === this.last ? 6 : 0);
        if (d < bestD) {
          bestD = d;
          best = t.i;
        }
      });
      return best;
    }

    #stateAt(event) {
      if (!this.states) return null;
      const [px, py] = this.#point(event);
      const Z = this.#currentZoom(performance.now());
      const x = (px - Z.x) / Z.k;
      const y = (py - Z.y) / Z.k;
      this.ctx.save();
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      const st = this.states.find(s => this.ctx.isPointInPath(s.path, x, y));
      this.ctx.restore();
      return st || null;
    }

    #onClick(event) {
      if (!this.ready) return;
      const i = this.#hit(event);
      if (this.sl.isMobile()) {
        if (i !== null) {
          this.sl.openStore(i, this.sl.sheet?.kind === 'state' ? 'state' : 'map');
          return;
        }
        const st = this.#stateAt(event);
        if (st && st.n > 0) this.sl.openState(st.name);
        else if (this.zoom) {
          this.sl.closeSheet();
          this.zoomTo(null);
        }
        return;
      }
      if (i === null) {
        const st = this.#stateAt(event);
        if (this.pop !== null) this.#closePop();
        if (st && st.n > 0 && st.name !== this.zoom) this.zoomTo(st.name);
        else if (!st && this.zoom) this.zoomTo(null);
        return;
      }
      const target = this.hits.find(t => t.i === i) || this.pillHits.find(t => t.i === i);
      this.sl.select(i, 'map');
      this.#openPop(i, target.x, target.y + (target.h ? target.h / 2 : 0));
    }

    #onMove(event) {
      if (this.moveRaf) return;
      this.moveRaf = requestAnimationFrame(() => {
        this.moveRaf = 0;
        if (!this.ready) return;
        const i = this.#hit(event);
        const st = i === null ? this.#stateAt(event) : null;
        const name = st && st.n > 0 ? st.name : null;
        this.canvas.style.cursor = i !== null || (name && name !== this.zoom) ? 'pointer' : 'default';
        if (i !== this.hover || name !== this.hoverState) {
          this.hover = i;
          this.hoverState = name;
          if (!this.loop) this.#draw();
        }
      });
    }

    /** Timeline / directory "see on map": scroll, zoom to the state, then open the card. */
    #goTo(index) {
      this.sl.scrollTo('map-canvas');
      const s = this.stores[index];
      const wait = this.reduced ? 50 : 500;
      setTimeout(() => {
        const pinned = index === 0 || index === this.last;
        // First and launch stores are shown on the all-India view: zoom back out if another state is open.
        if (s?.state && s.state !== this.zoom) this.zoomTo(pinned ? null : s.state);
        // The pin may still be animating in (the intro wave replays on re-entry): retry briefly.
        let tries = 0;
        const open = () => {
          const t = this.hits.find(x => x.i === index);
          if (t) this.#openPop(index, t.x, t.y);
          else if (++tries < 20) this.goToTimer = setTimeout(open, 200);
        };
        this.goToTimer = setTimeout(open, this.reduced ? 50 : 1000);
      }, wait);
    }

    /* ---------------- Popup card ---------------- */

    /** Same card as the mobile sheet (core storeCardTitle/Sub/Body), floated beside the pin. */
    #openPop(i, x, y) {
      const s = this.stores[i];
      const L = this.L;
      if (!s || !L || this.sl.isMobile()) return;
      const { esc, icon } = this.sl.helpers;
      const pop = this.refs.pop;
      const titleId = `${this.element.id || 'NmsSlMap'}-pop-title`;
      pop.innerHTML = `
        <div class="nms-sl-card__head">
          <div class="nms-sl-card__titles">
            <div class="nms-sl-card__title-row">
              ${this.sl.stateBadge(s.state)}<h3 class="nms-sl-card__title" id="${titleId}">${esc(this.sl.storeCardTitle(s))}</h3>
            </div>
            <span class="nms-sl-card__sub">${esc(this.sl.storeCardSub(s))}</span>
          </div>
          <button type="button" class="nms-sl-card__close" data-pop-close aria-label="${esc(this.sl.t.closeDetails)}">${icon('close', 20)}</button>
        </div>
        <div class="nms-sl-card__body">${this.sl.storeCardBody(s)}</div>`;
      pop.setAttribute('aria-labelledby', titleId);
      pop.hidden = false;
      // Beside the pin, kept inside the map box (popup is 375 wide).
      const W = pop.offsetWidth || 375;
      const H = pop.offsetHeight;
      const onLeft = x + W + 36 > L.w;
      this.popSide = onLeft ? 'l' : 'r';
      this.picked = i;
      pop.style.left = `${Math.round(onLeft ? Math.max(12, x - W - 18) : x + 18)}px`;
      pop.style.top = `${Math.round(Math.max(16, Math.min(L.h - H - 16, y - H / 2)))}px`;
      this.sl.fillRatings(pop);
      this.pop = i;
      this.#draw();
    }

    #closePop() {
      if (this.pop === null) return;
      this.pop = null;
      this.refs.pop.hidden = true;
      this.refs.pop.innerHTML = '';
      this.#draw();
    }

    #onPopClick(event) {
      if (event.target.closest('[data-pop-close]')) this.#closePop();
      else this.sl.onStoreCardClick(event);
    }

    /* ---------------- Chips + overlay ---------------- */

    #renderChips() {
      const { esc, icon } = this.sl.helpers;
      const entries = Object.entries(this.counts).sort((a, b) => a[0].localeCompare(b[0]));
      const total = this.stores.filter(s => !s.mock).length;
      const chips = [[null, this.labels.allIndia, total], ...entries.map(([name, n]) => [name, name, n])];
      // Each state's icon (State icon blocks); All India and states without one get the pin.
      const badge = name => {
        const src = name && this.sl.stateIcon(name);
        return `<span class="nms-sl-chip__icon" aria-hidden="true">${src ? `<img src="${esc(src)}" alt="" width="28" height="28" loading="lazy">` : icon('pin', 20)}</span>`;
      };
      const html = withIcon => chips.map(([name, label, n]) => `
        <button type="button" class="nms-sl-chip" data-state="${esc(name || '')}" aria-pressed="${name === this.zoom ? 'true' : 'false'}">
          ${withIcon ? badge(name) : ''}<span class="nms-sl-chip__label">${esc(label)}</span><span class="nms-sl-chip__count">${n}</span>
        </button>`).join('');
      [this.refs.toolbar, this.refs.aside].forEach(list => {
        if (!list) return;
        list.innerHTML = html(true);
        if (!list.dataset.bound) {
          list.dataset.bound = '1';
          list.addEventListener('click', event => {
            const chip = event.target.closest('[data-state]');
            if (chip) this.#onChip(chip.dataset.state || null);
          }, { signal: this.controller.signal });
        }
      });
    }

    #onChip(name) {
      if (this.sl.isMobile()) {
        if (name) this.sl.openState(name);
        else {
          this.sl.closeSheet(true);
          this.zoomTo(null);
        }
        return;
      }
      this.zoomTo(name);
      if (this.refs.wrap.getBoundingClientRect().top > window.innerHeight * .5) this.sl.scrollTo('map-canvas');
    }

    #renderOverlay() {
      const { fill } = this.sl.helpers;
      const mobile = this.L ? this.L.mobile : this.sl.isMobile();
      const D = this.labels;
      this.refs.zoomBar.hidden = !this.zoom;
      if (this.refs.fromPill) this.refs.fromPill.hidden = !!this.zoom;
      this.refs.zoomLabel.textContent = this.zoom ? fill(D.zoomLabel, { state: this.zoom, count: this.counts[this.zoom] || 0 }) : '';
      if (this.refs.fromLabel) this.refs.fromLabel.textContent = fill(D.fromLabel, { count: this.sl.totals().cities });
      this.refs.hint.textContent = this.zoom ? (mobile ? D.hintDotMobile : D.hintDotDesktop) : mobile ? D.hintStateMobile : D.hintStateDesktop;
    }

    #renderLegend() {
      if (!this.refs.legendOpen) return;
      this.refs.legendOpen.textContent = this.sl.helpers.fill(this.labels.legendOpen, { count: this.sl.totals().open });
      this.refs.legendCountdown.textContent = this.sl.countdown().text;
    }

    destroy() {
      this.controller.abort();
      cancelAnimationFrame(this.loop);
      clearTimeout(this.idleTimer);
      clearTimeout(this.resizeTimer);
      clearTimeout(this.goToTimer);
      this.near?.disconnect();
      this.seen?.disconnect();
      this.resize?.disconnect();
      super.destroy();
    }
  }

  const register = () => window.neemans.storeLaunch.register('[data-nms-sl-map]', NMSSlMap);
  if (window.neemans?.storeLaunch) register();
  else document.addEventListener('nms-sl:ready', register, { once: true });
})();
